import Vue from 'vue'
import { store } from '../store'
import PhoneNumber from 'awesome-phonenumber'
import Address from '@/types/Address.interface'
import Person, { Phone } from '@/types/Person.interface'
import Broadcast from '@/types/Broadcast.interface'
import User from '@/types/User.interface'
import Campaign from '@/types/Campaign.interface'
import Channel from '@/types/Channel.interface'
import Group from '@/types/Group.interface'
import TenantMembership from '@/types/TenantMembership.interface'
import Message from '@/types/Message.interface'
import Tenant from '@/types/Tenant.interface'
import Media from '@/types/Media.interface'
import ShortLink from '@/types/ShortLink.interface'
import moment from 'moment-timezone'
import router from '@/router/router'
export default {
    //  Parses an Address object into a single string
    addressString(address: Address): string {
        //  If the address does NOT exist, return empty string
        if (!address) return ''

        //  Create ref to address, so its easier to concat with
        const a = address

        //  Check if the address has valid info
        const isValid = a.line1 || a.city || a.country_subdivision || a.postalcode

        //  If the addres has no data return empty string
        if (!isValid) return ''

        //  Else, Assemble the address as a single string
        let addr = `${a.line1 || ''}, ${a.city || ''}, ${a.country_subdivision || ''} ${a.postalcode || ''}`

        //  Return the parsed address
        return addr
    },
    //  Returns a mdi-icon badge for a broadcast channel
    broadcastBadge(broadcast: Broadcast): string {
        //  Get the icon for the broadcast's group
        const icon = broadcast.group.query ? 'mdi-lightbulb-on' : 'mdi-account-multiple'

        //  Return the icon
        return icon
    },
    //  Returns the broadcast name, or group name
    broadcastName(broadcast: Broadcast) {
        if (broadcast.name) return broadcast.name

        return broadcast.group.name
    },
    //  Converts a UTC datetime to readable time in the tenant's timezone
    formattedDatetime(datetime: string): string {
        if (!datetime) return null

        //  Get the tenant's timezone
        const tz = store.getters.tenant.default_timezone || 'UTC'

        //  Parse the datetime, attaching the tenant's timezone
        const formatted = moment.utc(datetime).tz(tz).format('MMM D, Y, h:mma z')

        return formatted
    },
    //  Returns a human readable campaign type
    campaignType(campaign: Campaign): string {
        //  Select the human readable type
        const type = {
            auto_response: 'Autoresponder',
            campaign: 'Campaign'
        }[campaign.type]

        //  If a type was found return it
        if (type) return type

        //  Else, return the raw campaign type
        return campaign.type
    },
    //  Returns the channel assigned display string
    channelAssigned(channel: Channel): string {
        if (!channel.assigned_id || !channel.assigned) return 'Unassigned'

        //  Get the assigned user from the local store
        const assigned = store.getters.users.find(x => x.id === channel.assigned_id)

        //  If there is an assigned id, we want to add the image if possible
        const assignedHtml = (user: User): string | null => {
            //  If the user does NOT exist, return nothing
            if (!user) return null

            //  Else, If there is a logo route, just return the image html
            if (user?.logo_route) return `<div class="v-avatar" style="height:25px; width:25px; min-width:25px;"><img src="${user.logo_route.remote_link}" /></div>`

            //  Get the currently authenticated user
            const authUser = store.getters.user

            //  Check if the channel is assigned 'Me'
            const isMe = user.id === authUser.id

            //  Else, just reutrn their full name or email
            return isMe ? 'Me' : this.userFullNameOrEmail(user)
        }

        //  Return our assigned
        return `Assigned to: ${assignedHtml(assigned)}`
    },
    //  Return the color we style the channel with
    channelColor(channel: Channel): string {

        //  Check if the person is blocked or not
        const isBlocked = store.getters.isPersonBlocked(channel.person)

        //  If the contact is blocked, return red
        if (isBlocked) return 'red darken-2'

        //  Else, if our channel is closed, return grey
        if (channel.closed) return 'grey'

        //  Else, If we have no unread messages, return grey
        if (!channel.unread_messages) return '#616161'

        //  Else return primary
        return '#6142B0'
    },
    //  Returns the last message text, or media attachment text
    channelLastMsgTxt(channel: Channel): string {
        //  If the channel does NOT have a 'last_message' return nothing
        if (!channel.last_message) return null

        //  Else, grab our last message text
        const txt = this.parseCommentedText(channel.last_message.text)

        //  If the message does NOT have text, AND has a media attachment, return attachment message
        if (!txt && channel.last_message.media_id) return 'Attachment: 1 Image'

        //  If the last message is NOT outbound then just return the text
        if (!channel.last_message.outbound) return txt

        //  Else Get the user's name that wrote the last reply to the contact
        const authorName = this.personFullNameOrEmail(channel.last_message.author_min)

        //  Check if this is a comment or not
        const comment = channel.last_message.type === 'comment'

        //  Our keyword depends on if the type is a comment or not
        const keyword = comment ? 'Commented' : 'Replied'

        //  If we have an author name we append to last response
        if (authorName) return `<i>${authorName} ${keyword}:</i> ${txt}`

        //  Else we return the last response with replied appended 
        return `<i>${keyword}:</i> ${txt}`
    },
    //  Return the name of a channel
    channelName(channel: Channel): string {
        //  Check if the channel has a person attached
        const person = channel.person

        //  If a person exists, return the person's name
        if (person) return this.personFullNameOrEmail(person)

        //  Else, get the channels last message (we'll use this to find an msisdn)
        const msg = channel.last_message

        //  If a msg does NOT exist, we can't parse a name, so return default message
        if (!msg) return 'No name or number could be found'

        //  Else, get the current tenant's number (we need this for janky logic finding the msisdn)
        const n = store.getters.smsAccount.msisdn

        //  The msisdn can be the 'to' number or 'from' number, use 'n' to determine which one
        const msisdn = msg.to === n ? msg.from : msg.to

        //  Return the msisdn
        return this.phone(msisdn)
    },
    //  Determine if the channel is 'open' or 'closed'
    channelOpen(channel: Channel): boolean {
        //  Get the open and closed value from the channel
        const open = window.getProps(channel, ['open']) !== null
        const closed = window.getProps(channel, ['closed']) !== null

        //  Determine if the channel is open
        const isOpen = open && !closed

        // Return the result
        return isOpen
    },
    //  Return the status of the channel as a human readable string
    channelStatus(channel: Channel): string {
        //  Our container for our text
        let txt = ''

        //  If our channel is closed, return closed
        if (channel.closed) txt = 'Closed'

        //  If we have messages then we can proceed
        else if (channel.last_message) {
            //  Else, if we have unread messages, return new
            if (channel.unread_messages) txt = 'NEW'

            //  If no unread messages AND outbound, return 'REPLIED'
            else if (channel.last_message.outbound) txt = 'Replied'

            //  else, if no unread messages AND not outbound, return 'WAITING'
            else if (!channel.last_message.outbound) txt = 'Waiting'
        }

        //  Get our text color
        const color = this.channelColor(channel)

        //  Return styled text
        return `<span style="color:${color}" class="font-weight-bold">${txt}</span>`
    },
    //  Returns the text and text-color of the channel's current 'waiting' status.
    channelWaiting(channel: Channel): string {
        const message_class = `class="font-weight-bold"`

        //  Check if the channel is closed
        // if (channel.closed) return `<span style="color:grey; font-size:11px;" ${message_class}>Closed</span>`

        //  If there is no last message, return nothing
        if (!channel.last_message) return ''

        //  Else, check if the person is blocked
        const isBlocked = channel.person && store.getters.isPersonBlocked(channel.person)

        //  If the person is blocked, return 'blocked' message
        if (isBlocked) return `<span style="color:#A10000; font-size:11px;" ${message_class}>Blocked</span>`

        //  Else, get the last_message time (Sometimes 'last_message_time' is null, this is probably a server side error, but we account for it here)
        const lastTime = channel.last_message_time || channel.last_message.created_at

        //  Convert the datetime string into a human readable format
        const str = this.dateReadable(lastTime, true)

        //  Our parsed string defaults to our human readable datetime
        let parsed = str

        //  If our string includes 'sec' then put 'Now'
        if (str.includes('sec')) parsed = 'Now'

        //  Color is determined if the channel is 'Orange' status or 'primary' status. ( Primary: ie Open with last messange an inbound)
        const color = this.channelColor(channel)

        return `<span style="color:${color}; font-size:11px;" ${message_class}>${parsed}</span>`
    },
    //  Returns a date in format "MM/DD/YY, hh:mm am/pm" (ex: 06/19/20, 09:54 am) - good for dates where time is needed (message delivery/sent)
    chatDate(value: string): string {
        return moment.utc(value).local().format('MM/DD/YY, hh:mm a')
    },
    chatScheduleDate(value: string): string {
        //  If the value does NOT exist, return nothing
        if (!value) return null

        //  Else, return desired format
        return moment.utc(value).format('MM/DD/YY, hh:mm a')
    },
    //  Converts a vuetify color class into a vuetify text--color class
    colorToText(color: string): string {
        //  If the color does NOT exist, do nothing
        if (!color) return null

        //  Split the color by space (this check for multiple classes)
        const classes = color.split(' ')

        //  Get the base color (which should always exist)
        const base = classes[0]

        //  If the shading class exists, set it, else, set to null
        const shade = classes[1] ? classes[1] : null

        //  Create vuetify text classes from base and shade
        const baseClass = `${base}--text`
        const shadeClass = shade ? `text--${shade}` : ''

        //  Return the convert classes
        return `${baseClass} ${shadeClass}`
    },
    //  Converts a vuetify color class into a hex color
    colorToHex(color: string): string {
        //  If the color does NOT exist, do nothing
        if (!color) return null

        //  Check if the color is a 'theme' color
        const isTheme = ['primary', 'secondary', 'accent', 'error'].includes(color)

        if (isTheme) return Vue.prototype.$colors[color]

        //  Split the color by space (this check for multiple classes)
        const classes = color.split(' ')

        //  Get the base color (which should always exist)
        const base = classes[0]

        //  If the shading class exists, use it, else, default to vuetify 'base'
        const shade = classes[1] ? window._.camelCase(classes[1]) : 'base'

        //  Get the hex value for the color
        const hex = Vue.prototype.$colors[base][shade]

        //  Return the color in hex form
        return hex
    },
    //  Converts a vuetify color class into rbga opacity 0.15
    colorToOverlay(color: string): string {
        //  Convert the color to hex
        const hex = this.colorToHex(color)

        //  Create a function that converts hex to rbga
        const hexToRgbA = (hex) => {
            var c;
            if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
                c = hex.substring(1).split('');
                if (c.length == 3) {
                    c = [c[0], c[0], c[1], c[1], c[2], c[2]];
                }
                c = '0x' + c.join('');
                return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',0.15)';
            }
            throw new Error('Bad Hex');
        }

        //  Convert the hex to rbga
        const rbga = hexToRgbA(hex)

        //  Return the converted color
        return rbga
    },
    //  Returns a short rounded date (ex: "2 months", "1 hr", "3 wks") - good for dates where space is limited and exact time is not needed
    dateReadable(value: string, inbox: boolean = false): string {
        let response = null

        //  Use local time so we can make it relative to the user
        let created = moment.utc(value).local()
        let now = moment.utc().local()

        //  Fetch the difference between now and when the value was created
        let duration = moment.duration(now.diff(created))

        //  Parse possible extensions
        let durations = {
            years: { value: Math.floor(duration.asYears()), ext: 'years' },
            months: { value: Math.floor(duration.asMonths()), ext: 'months' },
            weeks: { value: Math.floor(duration.asWeeks()), ext: 'weeks' },
            days: { value: Math.floor(duration.asDays()), ext: 'days' },
            hours: { value: Math.floor(duration.asHours()), ext: 'hours' },
            minutes: { value: Math.floor(duration.asMinutes()), ext: 'min' },
            seconds: { value: Math.floor(duration.asSeconds()), ext: 'sec' },
            milliseconds: { value: Math.floor(duration.asMilliseconds()), ext: 'ms ago' }
        }

        //  If our inbox is true, then any durations that are under 1 day will have a timestamp instead
        if (inbox && durations.days.value < 1) return moment.utc(value).local().format('LT')

        //  Foreach duration, chose the first value greater than zero, set response to value and ext.
        for (var d in durations) {
            let value = durations[d]['value']
            if (value > 0) {

                //  If the duration is in milliseconds, override with 1 second
                if (d === 'milliseconds') response = '1 sec ago'

                //  Else, return the matching duration
                else response = `${value} ${durations[d]['ext']}`

                break
            }
        }

        return response
    },
    //  Returns date in format "MMM D, YYYY, hh:mm am/pm" (ex: Jun 19, 2020, 09:51 am) - very similar to chatDate
    dateTime(value: string, timeOnly: boolean): string {
        if (timeOnly) return moment.utc(value).local().format('hh:mma')
        const tz = store.getters.tenant.default_timezone || 'UTC'
        const timezone = moment().tz(tz).format('z')
        const time = moment.utc(value).local().format('MMM D, YYYY, hh:mma')
        return `${time} (${timezone})`
    },
    extensionsToString(extensions: Object): string {
        //  If the extensions don't exist, return empty string
        if (!extensions) return ''

        //  Create ref to extensions, so its easier to concat with
        const e = extensions
        
        //  Create our new string that we concat everything to
        let extensionStrings = '' 

        //  Iterate over each key value pair of the extensions associated with the contact, if no value exists skip that 
        //  extension option, otherwise we append it to our string to be shown in the calling component
        Object.keys(e).forEach((key) => {
            if (!e[key]) return
            const temp = key + ': ' + e[key] 
            extensionStrings += ' ' + temp + '<br>'
        })

        //  Return the parsed extensions
        return extensionStrings
    },
    //  Our human readable file size
    fileSize(size: number): string {
        //  We will only display in increments of either KB or MB or GB
        const gb = size / 1000000000

        if (gb >= 1) return `${gb.toFixed(2)} gb`

        const mb = size / 1000000

        //  If mb is greater than or equal to 1, then return it in 2 decimals
        if (mb >= 1) return `${mb.toFixed(2)} mb`

        //  Else, just return this in kb
        return `${(size / 1000).toFixed(2)} kb`
    },
    //  return the badge icon for a group
    groupBadge(group: Group): string {
        //  If the group is required, return 'lock'
        if (group.required) return 'mdi-lock'

        //  If the group is archived, return 'bookmark'
        if (group.archived) return 'mdi-bookmark'

        //  If the group is important, return, 'star'
        if (group.important) return 'mdi-star'

        //  If the group is a query group, reutrn, 'filter'
        if (group.query) return 'bi-filter'

        //  Else, there is no group badge, return nothing
        return null
    },
    //  Return the group name and persons_count
    groupFullname(group: Group): string {
        const fullname = `${group.name} (${group.persons_count})`
        return fullname
    },
    //  Return a group icon based on the group type
    groupIcon(group: Group) {
        //  If this is the requried opt-out group, return opt out icon
        if (group.name === 'Opted Out') return 'mdi-cellphone-erase'

        //  Else, if the group is bound to an Autoresponder, return 'flash' icon
        if (group.campaign_id) return 'bi-lightning-fill'

        //  Else, return the default group icon
        return 'mdi-account-multiple'
    },
    //  Returns group type (Filter or Static)
    groupType(group: Group) {
        //  If this is the 'opt-out' group return unique label
        if (group.name === 'Opted Out') return 'Opted Out'

        //  If the group is attached to a Autoresponder, return 'auto response'
        if (group.campaign_id) return 'Autoresponder'

        //  If the group is a query group, return 'Filter List'
        if (group.query) return 'Filter Group'

        //  Else, return generic list
        return 'Static Group'
    },
    //  Return the persons count for a group
    groupPersonsCount(group: Group) {
        /**
         * Hack v1.2.5
         * Lazy loading the persons_count for invoice groups is SUPER slow...like 30sec slow
         * So we ignore the persons count on those groups
         */

        //  If the group does NOT exist, return null
        if (!group) return null

        //  If the group has a query, we need to check for invoices
        if (group.query && group.query.queries.length > 0) {
            //  Else, check if the group is an invoices group
            const isInvoices = group.query.queries[0].has === 'invoices'
    
            //  If this is an invoices group
            if (isInvoices) return 'Dynamic members'
        }

        //  Else, return the person's count
        return `${group.persons_count} members`
    },
    //  Returns html string where searchtext is colored if searchtext is a substring of str
    highlight(str: string, searchtext: string, color: string): string {
        //  If the search string does NOT exist, return original str
        if (!searchtext || !str) return str

        //  Search for text to highlight, includes is case sensitive so we make the string all lowercase 
        //  so case mismatches still get highlighted
        if (str.toLowerCase().includes(searchtext.toLowerCase())) {
            //  Create regex for replace function
            const regex = new RegExp(searchtext, 'gi')

            //  Replace search text with colored mark
            return str.replace(regex, function (result) {
                return `<mark class="white ${color || 'primary'}--text">${result}</mark>`
            })
        }

        return str
    },
    //  Returns date where day is included (ex: "Friday, June 19, 2020, 10:00 am") - good for schedules where we want to see the day
    humanDate(value: string): string {
        return moment(value, 'YYYY-MM-DD h:mm:ss').format('LLLL')
    },
    //  Returns an mdi icon suitable to be inserted into an IListTile line
    iListIcon(icon: string): string {
        //  Create the icon style
        const style = 'font-size:18px; position:absolute; margin-top:-1px;'

        //  Craete the div that acts as a filler for the absolute position icon
        const div = '<div style="display:inline-block; min-width:18px;"></div>'

        //  Assemble the icon
        const i = `<span class="mdi ${icon}" style="${style}"></span>${div}`

        //  Return the specialy formatted icon
        return i
    },
    // Returns a string (outbound or inbound) depending on whether or not the message's outbound field is true or false
    inboundOutbound(isOutbound: boolean): string {
        // If our message was outbound we return text stating so, otherwise it's inbound
        if(isOutbound){
            return 'Outbound'
        }
        else{
            return 'Inbound'
        }
    },
    invoiceNumber(value: string): string {
        var number = value.replace(/\D+/g, "")

        return `Invoice #${number}`
    },
    //  Returns ISO string of date
    iso8601(datetime: string): string {
        const parsed = moment(datetime).toISOString(true)
        return parsed
    },
    //  Returns the icon of our media based on type
    mediaIcon(media: Media): string {
        //  Grab our media type
        const type = media.type

        //  Return a different icon based on type
        if (type === 'application/pdf') return 'mdi-file-pdf-outline'
        if (type.includes('image')) return 'mdi-file-image-outline'
        if (type.includes('video')) return 'mdi-video-image'
        return  'mdi-file-document'
    },
    //  Returns the type of media this is by parsing it from the type
    mediaIconText(media: Media): string {
        //  Grab our media type
        const type = media.type

        //  Text depends on our type
        if (type === 'application/pdf') return 'PDF'
        if (type.includes('image')) return 'Image'
        if (type.includes('video')) return 'Video'
        return  'File'
    },
    //  Returns the title of a tenant membership
    membershipTitle(membership: TenantMembership): string {
        //  If a tenant does not exist, return unknown
        if (!membership.tenant) return 'N/A'

        //  Else, return the tenant company name OR subdomain
        return membership.tenant.company_name || membership.tenant.subdomain
    },
    messageAuthor(message: Message): string {
        //  Messages will always have a to and from number so if the author isn't avaible (i.e anonymous contact) we just use the from number
        if (!message.author) return this.phone(message.from)
        
        //  We have to check that the author has a name_full set, as sometimes they might have just name_first but not full, if that's the case we also just
        //  use the from number. We cannot merge these checks since we need to see if author exists first
        if (!message.author.name_full) return this.phone(message.from)
        
        return message.author.name_full
    },
    //  Returns a human readable error, for an sms message
    messageError(message: Message): string {
        if (message.status === 'delivered') return null

        if (!message.standard_err) return null

        const error = {
            0: 'Delivered',
            1: 'Unknown',
            2: 'Invalid number',
            3: 'Invalid message',
            4: 'Anti-Spam Rejection',
            5: 'Destination Unreachable',
            6: 'Insufficient Funds',
            7: 'Carrier Error',
            8: 'Media Error'
        }[message.standard_err]

        return `(${error})`

    },
    messageIcon(message: Message): string {
        //  We use a unique icon  for failed messages so we check this status first since the rest of our icons are based on message type
        if (message.status === 'failed') return 'mdi-alert-circle'

        const icon = {
            'send': 'mdi-reply',
            'broadcast': 'bi-megaphone-fill',
            'autoresponder': 'bi-lightning-fill',
            'officehours': 'mdi-clock',
            'form': 'mdi-form-select',
            'chatbot': 'bi-chat-square-fill',
            'scheduled': 'mdi-message',
        }[message.type]

        //  Inbound messages have no type so this allows us to assign an icon to them, possible that types not in our map will get this icon as well but not the 
        //  end of the world
        if (!icon) return 'mdi-message-reply-text'

        return icon
    },
    //  Return the human readable status of a message
    messageStatus(message: Message): string {
        const status = message.status

        //  If the message has no status, return 'unknown'
        if (!status) return 'unknown'

        //  Else, select the status text
        let text = {
            delivered: 'Delivered Successfully',
            sent: 'Message Sent',
            failed: 'Failed'
        }[status]

        //  Select the status color
        const color = {
            delivered: 'primary--text',
            sent: 'primary--text',
            failed: 'red--text text--darken-2'
        }[status]

        //  Return assembled html status
        return `<span class="${color}">${text}</span>`
    },
    messageTo(message: Message): string {
        //  Messages will always have a to number but not always a recipient (i.e anonymous contact) so if we don't have a recipient we return the to number
        if (!message.recipient) return this.phone(message.to)
        
        //  Even though we have a recipient we may not have a full name as names aren't required so we check for this as well
        if (!message.recipient.name_full) return this.phone(message.to)

        return message.recipient.name_full
    },
    messageType(message: Message): string {
        //  By MCS request we map certain types to say something else than the DB definitions, for those that don't require adjustment we just return them
        if (message.status === 'failed') return 'Failed'
        if (message.type === 'send') return 'Sent'
        if (!message.outbound) return 'Received'
        
        return message.type
    },
    //  Return the North American msisdn of a valid 10-digit number
    msisdn(phone: string): string | null {
        //  If the phone does NOT exist return null
        if (!phone) return null

        //  Instantiate a new 'awesome-phonenumber'
        const p = new PhoneNumber(phone, 'US');

        //  If the phone is NOT valid, return unformatted msisdn
        if (!p.isValid()) phone

        //  Else get the national format
        let e164 = p.getNumber('e164');

        //  Convert e164 to msisdn
        const formatted = e164.replace('+', '')

        return formatted
    },
    //  Convert url text to clickable links, replace line breaks with html <br>
    parseMessageText(text: string, outbound: boolean = false, comment: boolean = false): string {
        //  This replaces links with hrefs
        if (text.includes('https://') || text.includes('http://')) {
            //  Wrap URLs in html link tags
            text = text.replace(/(https?:\/\/[^\s]+)/g, function (url) {

                //  Our callback for the hrefs
                window.goToLink = (url: string, isApp: boolean) => {
                    window.open(url, isApp ? '_system' : '_blank')
                }

                //  Our styles for this element
                const style = `display:inline; color:${outbound ? 'white' : '#6142b0'}; text-decoration:underline; cursor:pointer;`

                //  Our HTML element
                return `<v-col style="${style}" class="pa-0" onclick="goToLink('${url}', ${store.getters.isApp})">${url}</v-col>`
            })
        }

        //  Replace new line characters with html breakpoints
        text = text.replace(/\n/g, '<br>')

        //  If this is NOT a comment then we are done
        if (!comment) return text

        //  Grab our users
        const users = store.getters.users

        //  Replace @Team with a highlighted version of itself
        text = text.replace("@Team", (str: string) => {
            return `<span style="background-color: #6142B0; color: white; padding: 5px; border-radius: 10px;">${str}</span>`
        })

        //  Replace all occurrences of @User-ID- with the user name
        text = text.replace(/@User-ID-\d*/g, (str: string) => {
            //  Grab the id from the string
            const id = str.slice(9)

            //  Grab the user matching this id
            const user = users.find(x => x.id == id)

            //  If there is no user, just return the same string
            if (!user) return str

            //  Get the filtered name
            const name = this.userFullNameOrEmail(user)

            //  Our callback for navigating to a user
            window.goToUser = (id) => {
                //  If no id exists, do nothing
                if (id === 'Team') return router.push('/teammates')
                //  Push to the teammate
                router.push(`/teammates?user=${id}`)
            }

            //  Return an HTML element
            return `<span style="background-color: #6142B0; color: white; padding: 5px; border-radius: 10px; cursor:pointer;" onclick="goToUser(${id})">@${name}</span>`
        })

        //  Return parsed text
        return text
    },
    parseCommentedText(text: string, formatted: boolean = true): string {
        //  If no text, just return itself
        if (!text) return text

        //  Grab our users
        const users = store.getters.users

        //  Replace all occurrences of @User-ID- with the user name
        text = text.replace(/@User-ID-\d*/g, (str: string) => {
            //  Grab the id from the string
            const id = str.slice(9)

            //  Grab the user matching this id
            const user = users.find(x => x.id == id)

            //  If there is no user, just return the same string
            if (!user) return str

            //  Get the filtered name
            const name = this.userFullNameOrEmail(user)

            //  Return an HTML element
            return formatted ? `<span>@${name}</span>` : `@${name}`
        })

        //  Replace our weird whitespace with a valid whitespace
        text = text.replaceAll('﻿', '')

        //  Return our text
        return text
    },
    //  Return a string in Pascal case
    pascalCase(str: string): string {
        //  we cheat here and use lodash
        return window._.upperFirst(window._.camelCase(str))
    },
    //  Returns the color the person is styled with
    personColor(person: Person): string {
        //  Check if the person is an inactive qbo customer
        const qboInactive = person.source === 'qbo' && person.source_status == 0

        //  Check if the person is an inactive xero contact
        const xeroInactive = person.source === 'xero' && person.source_status !== 'ACTIVE'

        //  If the person is inactive in Qbo OR Xero, return grey color
        if (qboInactive || xeroInactive) return 'grey'

        //  Else, return default primary color
        return 'primary'
    },
    //  Returns phone_default or free_form_number of person if exists
    personDefaultPhone(person: Person): string {
        const p = window.getProps(person, ['phone_default']);

        //  If there is no default phone, return empty
        if (!p) return ''

        //  Fetch the msisdn of the person's default phone
        const msisdn = p.msisdn

        //  Get our formatted number
        let formatted = msisdn ? this.phone(msisdn) : this.phone(p.free_form_number)

        //  Check if the phone is opted-out
        const isOptedOut = store.getters.optedOut.find(x => x === msisdn)

        //  If the phone is opted out, append the opted-out icon
        if (isOptedOut) formatted = formatted + '<span class="ml-1 mdi mdi-cellphone-erase"></span><span class="tiny">(opted-out)</span>'

        //  Else, return raw msisdn
        return formatted
    },
    //  Tries to return a name for a person using all available object values
    personFullNameOrEmail(person: Person): string {
        //  If the person does NOT exist, return nothing
        if (!person) return null

        //  Try to assemble a name from parts and return it
        let name = null
        if (person.name_family && !person.name_given) name = person.name_family
        if (person.name_given && !person.name_family) name = person.name_given
        if (person.name_given && person.name_family) name = `${person.name_given} ${person.name_family}`
        if (name) return name

        //  If full name is set, return it
        if (person.name_full) return person.name_full

        //  Still null?  Check if the person is a user
        if (person.user_id) {
            //  Find our user with this user id
            const user = store.getters.users.find((x: User) => x.id === person.user_id)

            //  If not found, return 'n/a'
            if (!user) return 'n/a'

            //  Run our filter on this user
            return this.userFullNameOrEmail(user)
        }

        //  Still Null?  try to return the persons email
        const email = window.getProps(person, ['email_default', 'address'], null)
        if (email) return email

        //  Still Null?  try to return the person's phone
        const phone = window.getProps(person, ['phone_default', 'free_form_number'], null)
        if (phone) return this.phone(phone)

        //  Still Null?  Nothing else we can do ¯\_(ツ)_/¯
        return 'n/a'
    },
    //  Tries to return a name for a person using all available object values
    personFullNameOrPhone(person: Person): string {
        //  If the person does NOT exist, return nothing
        if (!person) return null

        //  Try to assemble a name from parts and return it
        let name = null
        if (person.name_family && !person.name_given) name = person.name_family
        if (person.name_given && !person.name_family) name = person.name_given
        if (person.name_given && person.name_family) name = `${person.name_given} ${person.name_family}`
        if (name) return name

        //  If full name is set, return it
        if (person.name_full) return person.name_full

        //  Still null?  Check if the person is a user
        if (person.user_id) {
            //  Find our user with this user id
            const user = store.getters.users.find((x: User) => x.id === person.user_id)

            //  If not found, return 'n/a'
            if (!user) return 'n/a'

            //  Run our filter on this user
            return this.userFullNameOrPhone(user)
        }

        //  Still Null?  try to return the person's phone
        const phone = window.getProps(person, ['phone_default', 'free_form_number'], null)
        if (phone) return this.phone(phone)

        //  Still Null?  try to return the persons email
        const email = window.getProps(person, ['email_default', 'address'], null)
        if (email) return email

        //  Still Null?  Nothing else we can do ¯\_(ツ)_/¯
        return 'n/a'
    },
    //  Return the proper icon for a person
    personIcon(person: Person): string {
        // If the person is a user, return the user icon
        if (person.user_id) {
            //  Find our user with this user id
            const user = store.getters.users.find((x: User) => x.id === person.user_id)

            //  If not found, return 'n/a'
            if (!user) return 'mdi-account'

            //  Run our filter on this user
            return this.userIcon(user)
        }

        // If the person is blocked, return the account cancel icon
        if (person.phone_default?.blocked) return 'mdi-account-cancel'

        //  If the person is a qbo customer return check mark
        if (person.source === 'qbo') return 'mdi-check-circle'

        // Else, return default person icon
        return 'mdi-account'
    },
    personInboxName(person: Person): string {
        //  If the person does not exist return null
        if (!person) return null

        //  Try to assemble a name from parts
        let name = null
        if (person.name_given && person.name_family) name = `${person.name_given} ${person.name_family[0]}`
        else if (person.name_given && !person.name_family) name = person.name_given
        else if (!person.name_given && person.name_family) name = person.name_family
        if (name) return name

        //  Still null?  Check if the person is a user
        if (person.user_id) {
            //  Find our user with this user id
            const user = store.getters.users.find((x: User) => x.id === person.user_id)

            //  If not found, return 'n/a'
            if (!user) return 'n/a'

            //  Run our filter on this user
            return this.userInboxName(user)
        }

        //  Still Null?  try to return the persons email
        const email = window.getProps(person, ['email_default', 'address'], null)
        if (email) return email

        //  Still Null?  try to return the person's phone
        const phone = window.getProps(person, ['phone_default', 'free_form_number'], null)
        if (phone) return this.phone(phone)

        //  Still Null?  Nothing else we can do ¯\_(ツ)_/¯
        return 'n/a'
    },
    //  Return the initials for a person
    personInitials(person: Person): string {
        const first = person.name_given ? person.name_given.charAt(0) : ''
        const last = person.name_family ? person.name_family.charAt(0) : ''
        return `${first}${last}`
    },
    //  Return the msisdn OR free_form_number of a person's phone
    personPhone(phone: Phone): string {
        //  If there is no phone return empty string
        if (!phone) return ''

        //  Fetch the msisdn of the person's default phone
        const msisdn = phone.msisdn

        //  If the msisdn does NOT exist, return unformatted free-form-number
        if (!msisdn) return phone.free_form_number

        //  Instantiate a new 'awesome-phonenumber'
        const p = new PhoneNumber(msisdn, 'US');

        //  If the phone is NOT valid, return unformatted msisdn
        if (!p.isValid()) return this.phone(msisdn)

        //  Else get the national format
        let formatted = p.getNumber('national');

        return formatted
    },
    //  Returns type of person (User, QuickBooks Customer, or Contact)
    personType(person: Person): string {
        //  If a person does NOT exist, return nothing
        if (!person) return null

        //  If a person is blocked, prepend 'Blocked'
        const blocked = person.phone_default?.blocked ? 'Blocked ' : ''

        //  If the person is a user, return the user role
        if (person.user_id) {
            //  Find our user with this user id
            const user = store.getters.users.find((x: User) => x.id === person.user_id)

            //  If not found, return 'n/a'
            if (!user) return `${blocked}Contact`

            //  Run the filter on our user
            return store.getters.userRole(user)?.name || 'User Removed'
        }
        //  Else, if the person is a qbo customer...
        else if (person.source === 'qbo') {

            //  Check if the person is an inactive qbo customer
            const qboInactive = person.source_status == 0

            //  If the qbo customer is inactive, return 'Archived
            if (qboInactive) return 'Archived'

            // Else, return qbo type
            return `${blocked}QuickBooks Customer`
        }
        //  Else, if the preson is a xero contact
        else if (person.source === 'xero') {
            if (person.source_status === 'ARCHIVED') {
                return 'Archived'
            }
            return `${blocked}Xero Contact`
        }
        //  Else, return generic 'contact'
        else {
            return `${blocked}Contact`
        }
    },
    //  Turns a non-formatted phone number (ex: "12345678901") into readable number (ex: "(234) 567-8901")
    phone(value: string): string {
        //  If the value does NOT exist, return null
        if (!value) return null

        //  Remove non-numeric
        let numeric = value.replace(/\D/g, '');

        //  Instantiate a new 'awesome-phonenumber'
        const phone = new PhoneNumber(numeric, 'US')

        //  If the phone is valid, return national format
        if (phone.isValid()) return phone.getNumber('national')

        //  Else, trim numeric value
        if (numeric.length > 10) {
            numeric = numeric.slice(value.length - 10, value.length)
        }

        //  Format the numeric number
        const formatted = numeric.replace(/[^0-9]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')

        //  Use older formatter to return value
        return formatted
    },
    //  Remove invalid characters, trim to ten-digits
    phoneTrim(value: string): string {
        //  If the value does NOT exist, return null
        if (!value) return null

        //  Remove non-numeric
        value = value.replace(/\D/g, '');

        if (value.length > 10) {
            value = value.slice(value.length - 10, value.length)
        }

        return value
    },
    // Formats string such that the first letter is uppercae and the rest are lowercase
    punctuate(str: string): string {
        return str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase()
    },
    //  Returns the icon for our short link route based on what type of short link route it is
    routeIcon(route: ShortLink): string {
        //  Just use a pseudo switch
        return {
            form: 'bi-ui-checks',
            invoice: 'bi-cash',
            web: 'bi-link'
        }[route.type] || 'bi-file-image-fill'
    },
    //  Formats a schedule's 'next_send' time
    scheduleNextSendTime(value: string): string {
        //  Get the tenant's timezone
        const tz = store.getters.tenant.default_timezone || 'UTC'

        //  Parse the next_send datetime, attaching the tenant's timezone
        const formatted = moment.tz(value, tz).format('MMM D, Y, h:mma z')

        return formatted

    },
    snakeCase(str: string): string {
        return str && str
            .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            .map(x => x.toLowerCase())
            .join('_');
    },
    //  Returns a price with 2 decimal point precision
    stripePrice(price: any): string {
        //  Stripe annual plan price per month is passed in the subscription's metadata as a string and in stripe's pricing format
        if (isNaN(price)) {
            price = parseInt(price);
        }
        let p = price * 0.01
        return '$' + p.toFixed(0)
    },
    stripeCreditPrice(price: string): string {
        return '$' + (parseFloat(price) * 0.01).toFixed(3)
    },
    //  Returns the tenant name
    tenantName(tenant: Tenant): string {
        //  If the tenant does NOT exist, return nothing
        if (!tenant) return null

        //  If the company_name is set, return it
        if (tenant.company_name) return tenant.company_name

        //  Else, return the tenant subdomain
        return tenant.subdomain
    },
    //  Returns a human readable timestamp - used for billing dates returned to us by Stripe
    timestamp(value: number): string {
        return moment.unix(value).format('MMM D, Y')
    },
    //  Returns string trimmed down to 14 characters with '...' appended (17 characters total)
    trim(str: string): string {
        if (str) {
            return str.length >= 18 ? `${str.substring(0, 15)}...` : str
        }
    },
    //  Reduce an html string to a specific character length
    truncateHTML(value: string, length: number): string {
        //  If the value does NOT exist, return empty string
        if (!value) return "";

        //  Convert the value into a string
        value = value.toString();

        //  Remove line breaks from the string
        value = value.replace(/(\r\n|\n|\r)/gm, " ");

        //  Remove HTML '<br>' from the string
        value = value.replace(/<br\s*[\/]?>/gi, '');

        //  If the string is longer than the length, trim and add elipse
        if (value.length > length) {
            return value.substring(0, length) + "...";
        } else {
            return value;
        }
    },
    //  Tries to construct full name using first and last name of user, otherwise return their email
    userFullNameOrEmail(user: User): string {
        const _first = window.getProps(user, ['firstname']) || ''
        const _last = window.getProps(user, ['lastname']) || ''

        if (_first || _last) {
            return `${_first} ${_last}`
        }
        return user.email
    },
    //  Tries to construct full name using first and last name of user, otherwise return their phone
    userFullNameOrPhone(user: User): string {
        const _first = window.getProps(user, ['firstname']) || ''
        const _last = window.getProps(user, ['lastname']) || ''

        if (_first || _last) {
            return `${_first} ${_last}`
        }
        return this.phone(user.mobilephone)
    },
    //  Returns icon string based on user's role
    userIcon(user: User): string {
        //  Grab the role
        const role = store.getters.userRole(user)

        //  Get the name from the role
        const name = role?.name

        //  Return our icon based on role
        return {
            Admin: 'mdi-shield-star',
            Manager: 'mdi-shield-account',
            Operator: 'mdi-shield-account-outline'
        }[name] || 'mdi-shield-off-outline'
    },
    userInboxName(user: User): string {
        //  Try to construct a name
        let name = null
        if (user.firstname && user.lastname) name = `${user.firstname} ${user.lastname[0]}`
        else if (user.firstname && !user.lastname) name = user.firstname
        else if (!user.firstname && user.lastname) name = user.lastname
        if (name) return name

        return this.phone(user.mobilephone)
    },
    //  Returns first letter of user's first and last name
    userInitials(user: User): string {
        //  Check if the user has a name set
        const hasName = user.firstname || user.lastname

        //  If the user does NOT have any names, return first letter of their email
        if (!hasName) return user.email.charAt(0);
       
        //  Else, create a container for our initials
        let result = ''

        //  If the user has a firstname, add its initial
        if (user.firstname) result += user.firstname.charAt(0);

        //  If the user has a lastname, append its intial
        if (user.lastname) result += user.lastname.charAt(0);

        //  Return the user's intials
        return result
    },
    // Returns correctly formatted phone number
    userPhone(user:User): string {
        return this.phone(user.mobilephone)
    },
    //  Filters a number to have commas (i.e. 1000 becomes 1,000)
    withCommas(integer: number): string {
        return integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
}
